import { useTranslation } from 'react-i18next';
import type { ImplementerCandidate } from '../../../lib/bug-evaluation-api';

/** @graph-connects none */
interface ImplementerCandidatesProps {
  /** 候補を引いた起因案件キー */
  causeKey: string;
  /** 候補（件数の降順。API が並び順を決める） */
  candidates: ImplementerCandidate[];
  /** 今フォームで選ばれている実装担当者 */
  selected: string;
  /** 候補を選んだとき（在籍者に紐づく候補のみ呼ばれる） */
  onPick: (nickName: string) => void;
}

/**
 * 起因案件の実装担当者の候補を根拠つきで出し、クリックで選べるようにする。
 *
 * **候補は答えではない。** 件数が最多の人が当該バグを書いたとは限らない（実測では、記録上の
 * 帰属が 1 件しか書いていない人だった案件がある）ので、1 人に絞らず全員を根拠つきで出して
 * 人が選ぶ。根拠の PR を開けるようにしてあるのがこの画面の主目的。
 *
 * ニックネームが付いていない候補は在籍者に紐づかない（退職者等）ため**選べない**。選べない
 * ことを隠さずに出すのは、「候補が居ない」と「候補は居るが確定先に選べない」を人が区別
 * できるようにするため。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 起因案件の実装担当者の候補を根拠（リポジトリ・PR・ブランチ）つきで並べ、クリックで確定欄へ入れる
 * @graph-connects none
 */
export function ImplementerCandidates({
  causeKey,
  candidates,
  selected,
  onPick,
}: ImplementerCandidatesProps) {
  const { t } = useTranslation();

  return (
    <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <span className="text-xs font-medium text-[var(--color-text-secondary)]">
          {t('bugEvaluation.implementerCandidates', '実装担当者の候補（GitHub の PR から）')}
        </span>
        <span className="text-xs text-[var(--color-text-muted)]">{causeKey}</span>
      </div>

      {candidates.length === 0 ? (
        <p className="mt-2 text-xs text-[var(--color-text-muted)]">
          {t(
            'bugEvaluation.implementerCandidatesEmpty',
            'GitHub にこの起因案件キーの痕跡がありません。人が決めてください。',
          )}
        </p>
      ) : (
        <>
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">
            {t(
              'bugEvaluation.implementerCandidatesHint',
              '件数が最多の人が当該バグを書いたとは限りません。根拠の PR を見て選んでください。',
            )}
          </p>
          <ul className="mt-2 space-y-2">
            {candidates.map((candidate) => (
              <li key={candidate.githubLogin} className="text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  {candidate.nickName === null ? (
                    <span className="text-[var(--color-text-muted)]">
                      {t(
                        'bugEvaluation.implementerCandidateUnlinked',
                        '在籍者に紐づきません（退職者の可能性）',
                      )}
                    </span>
                  ) : (
                    <button
                      type="button"
                      aria-label={candidate.nickName}
                      aria-pressed={selected === candidate.nickName}
                      onClick={() => {
                        onPick(candidate.nickName ?? '');
                      }}
                      className={`rounded-md border px-2 py-0.5 text-sm ${
                        selected === candidate.nickName
                          ? 'border-[var(--color-accent-primary)] text-[var(--color-accent-primary)]'
                          : 'border-[var(--color-border)] text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]'
                      }`}
                    >
                      {candidate.nickName}
                    </button>
                  )}
                  <span className="text-xs text-[var(--color-text-muted)]">
                    {candidate.githubLogin} ・ {candidate.prCount} ・ {candidate.source}
                  </span>
                </div>
                <ul className="mt-1 space-y-0.5 pl-2">
                  {candidate.evidence.map((evidence, index) => (
                    <li
                      // コミット経路は number が 0 のため、同一 repo の根拠が複数あると repo#number だけでは衝突する
                      key={`${evidence.repo}#${String(evidence.number)}#${String(index)}`}
                      className="text-xs text-[var(--color-text-secondary)]"
                    >
                      {evidence.url === null ? (
                        <span>{evidence.repo}</span>
                      ) : (
                        <a
                          href={evidence.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[var(--color-accent-primary)] hover:underline"
                        >
                          {evidence.repo}#{evidence.number}
                        </a>
                      )}{' '}
                      {evidence.headRef === '' ? evidence.title : evidence.headRef}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
